"use strict";

const ResourceFilters = function () {
  var spatialFilters = function (unfilteredData, filters) {
    return unfilteredData.filter((datum) => {
      return spatialFilter(
        {
          drains_to_rw: datum.catchment?.drains_to_rw || datum.drainsToRw,
          drains_to_c: datum.catchment?.drains_to_c || datum.drainsToC,
          catchid: datum.catchment?.catchid || datum.catchmentName,
          highwayName: datum.highways?.highwayName,
          maintenanceZoneName: datum.maintenanceZones?.maintenanceZoneName,
        },
        filters,
      );
    });
  };

  var spatialFilter = function (
    { drains_to_rw, drains_to_c, catchid, highwayName, maintenanceZoneName } = {},
    filters,
  ) {
    return (
      (passSpatialFilter(drains_to_rw, filters.receivingWaters, "Other") ||
        passSpatialFilter(drains_to_c, filters.receivingWaters, "Other")) &&
      passSpatialFilter(catchid, filters.catchments, "outside boundary") &&
      passSpatialFilter(highwayName, filters.highways, "Outside Highways") &&
      passSpatialFilter(maintenanceZoneName, filters.maintenanceZones, "Outside Maintenance Zones")
    );
  };

  var passSpatialFilter = function (dataProperty, filterProperty, outsideKey = null) {
    if (filterProperty?.length) {
      if (outsideKey && filterProperty.includes(outsideKey)) {
        return filterProperty.includes(dataProperty) || !dataProperty;
      } else {
        return filterProperty.includes(dataProperty);
      }
    }

    return true;
  };

  var passIsoDateFilter = function (isoDateString, dateFrom, dateTo) {
    if (dateFrom || dateTo) {
      const date = DateTime.parseIsoDate(isoDateString);
      return passDateFilter(date, dateFrom, dateTo);
    }

    return true;
  };

  var passDateRangeOverRangeFilter = function (
    isoDateFrom,
    isoDateTo,
    dateFromFilter,
    dateToFilter,
  ) {
    if (dateFromFilter || dateToFilter) {
      const dateFrom = DateTime.parseIsoDate(isoDateFrom);
      const dateTo = DateTime.parseIsoDate(isoDateTo);

      return passDateFilter(dateFrom, dateFromFilter, dateToFilter, dateTo);
    }

    return true;
  };

  var isDateFilterSet = function (filterObj) {
    return filterObj?.from || filterObj?.to;
  };

  var passDateFilter = function (dateToCheckTo, dateFrom, dateTo, dateToCheckFrom = null) {
    dateFrom = DateTime.parseIsoDate(dateFrom);
    dateTo = DateTime.parseIsoDate(dateTo);
    var passFrom = true;
    var passTo = true;

    if (!(dateToCheckTo instanceof Date)) {
      return false;
    }

    if (dateFrom) {
      passFrom = dateToCheckTo >= dateFrom;

      if (!passFrom && dateToCheckFrom) {
        passFrom = dateToCheckFrom >= dateFrom;
      }
    }

    if (dateTo) {
      const nextDay = setNextDay(dateTo);
      passTo = dateToCheckTo < nextDay;

      if (!passTo && dateToCheckFrom) {
        passTo = dateToCheckFrom < nextDay;
      }
    }

    return passFrom && passTo;
  };

  var setNextDay = function (date) {
    date.setDate(date.getDate() + 1);
    return date;
  };

  var passDatesFilter = function (dates, dateFrom, dateTo) {
    if (!dateFrom && !dateTo) {
      return true;
    }

    return dates.some((date) => passDateFilter(date, dateFrom, dateTo));
  };

  var passSearchStringFilter = function (datum, filters, searchFields) {
    if (filters?.searchString) {
      const str = filters.searchString.toLowerCase();

      return searchFields.some((field) => datum[field]?.toLowerCase().includes(str));
    }

    return true;
  };

  var spatialFiltersAreSet = function (filters) {
    const spatialFilters = ["receivingWaters", "catchments", "highways", "maintenanceZones"];

    for (const filterField of spatialFilters) {
      if (filters[filterField]?.length) {
        return true;
      }
    }

    return false;
  };

  var toDoSpatialFiltersAreSet = function (toDoFilters) {
    if (!toDoFilters) {
      return false;
    }

    for (const subject in toDoFilters) {
      if (toDoFilters[subject].allSpatial === false) {
        return true;
      }
    }

    return false;
  };

  var passUnknownWhiteListFilter = function (data, list, whiteList) {
    if (!Array.isArray(list)) {
      return true;
    }

    if (list.includes(data)) {
      return true;
    } else if (list.includes("unknown")) {
      return !whiteList.includes(data);
    }

    return false;
  };

  var passTrueFalseBooleanFilter = function (data, filterList) {
    if (!filterList) {
      return true;
    }

    const filter = filterList.map(function (filter) {
      if (filter === "true") {
        return true;
      } else if (filter === "false") {
        return false;
      }
      return filter;
    });

    return passUnknownWhiteListFilter(data, filter, [true, false]);
  };

  var passToDoFilters = function (datum, filters, filterFunction = () => true) {
    const toDoFilters = filters.toDoFilters;

    if (!passSelectedSubject(datum, filters)) {
      return false;
    }

    if (toDoFilters === undefined) {
      return true;
    }

    for (var i = 0; i < datum.toDoSubjects?.length; i++) {
      const subject = datum.toDoSubjects[i];
      const subjectFilters = toDoFilters[subject];

      if (subjectFilters !== undefined && !filterFunction(datum, subjectFilters, subject)) {
        return false;
      }
    }

    return datum.toDoSubjects?.length !== 0;
  };

  var passSelectedSubject = function (datum, filters) {
    if (!filters.selectedSubject) {
      return true;
    }

    return datum.toDoSubjects?.includes(filters.selectedSubject);
  };

  var hasToDoSubject = function (datum, toDoSubjects) {
    if (!toDoSubjects) {
      return true;
    }

    for (const subject of toDoSubjects) {
      if (datum.toDoSubjects?.includes(subject)) {
        return true;
      }
    }

    return false;
  };

  var getIdSet = function (allData) {
    const idSet = new Set();

    for (const datum of allData) {
      idSet.add(datum.id);
    }

    return idSet;
  };

  var filterOnline = async function (
    unfilteredData,
    loadingScreen,
    getLayerDataFunction,
    options = {},
  ) {
    const filteredData = await getLayerDataFunction(true, loadingScreen, options);
    const idSet = getIdSet(filteredData);

    return unfilteredData.filter((datum) => idSet.has(datum.id));
  };

  var addDateParam = function (params, key, from, to) {
    if (from) {
      params[`${key}From`] = GeoServerFilterFunctions.formatSqlDateFrom(from);
    }
    if (to) {
      params[`${key}To`] = GeoServerFilterFunctions.formatSqlDateTo(to);
    }
  };

  var passToDoAssigneeFilter = function (datum, filters, toDoSubjects) {
    if (!toDoSubjects || !filters.assignee?.length) {
      return true;
    }

    const assigneeIds = getCleanAssigneeFilter(filters);

    for (const subject of toDoSubjects) {
      const subjectAssigneeId = datum[subject]?.isDue
        ? (datum[subject].assignee?.id ?? null)
        : undefined;

      if (subjectAssigneeId !== undefined && assigneeIds.includes(subjectAssigneeId)) {
        return true;
      }
    }

    return false;
  };

  var getCleanAssigneeFilter = function (filters) {
    return filters.assignee.map((id) => (id === "null" ? null : parseInt(id)));
  };

  var passToDoDueDateFilter = function (datum, filters, toDoSubjects) {
    if (!toDoSubjects || (!filters.dueDate?.from && !filters.dueDate?.to)) {
      return true;
    }

    for (const subject of toDoSubjects) {
      const date = DateTime.parseIsoDate(datum[subject]?.dueDate);

      if (
        date instanceof Date &&
        passDateFilter(date, filters.dueDate?.from, filters.dueDate?.to)
      ) {
        return true;
      }
    }

    return false;
  };

  var getSmartToDoFilters = function () {
    if (!Filters.shouldUseToDoSmartFilters()) {
      return {};
    }

    const userToDoSubjects = DataViewFunctions.getUserEnabledToDoSubjects();

    return {
      dueDate: (datum, filters) =>
        passToDoDueDateFilter(
          datum,
          filters,
          getToDoSubjectsFromFilters(filters, userToDoSubjects),
        ),
      assignee: (datum, filters) =>
        passToDoAssigneeFilter(
          datum,
          filters,
          getToDoSubjectsFromFilters(filters, userToDoSubjects),
        ),
      toDoSubject: (datum, filters) =>
        hasToDoSubject(datum, getToDoSubjectsFromFilters(filters, userToDoSubjects)),
    };
  };

  var getToDoSubjectsFromFilters = function (filters, userToDoSubjects) {
    if (filters.selectedSubject) {
      return [filters.selectedSubject];
    } else {
      return userToDoSubjects;
    }
  };

  var passSmartToDoFilters = function (datum, filters, toDoSubjects) {
    return (
      passToDoDueDateFilter(datum, filters, toDoSubjects) &&
      passToDoAssigneeFilter(datum, filters, toDoSubjects)
    );
  };

  return {
    spatialFilters,
    spatialFilter,
    spatialFiltersAreSet,
    passSearchStringFilter,
    passIsoDateFilter,
    passDateFilter,
    passDatesFilter,
    addDateParam,
    passUnknownWhiteListFilter,
    passTrueFalseBooleanFilter,
    passToDoFilters,
    passDateRangeOverRangeFilter,
    toDoSpatialFiltersAreSet,
    filterOnline,
    isDateFilterSet,
    passToDoAssigneeFilter,
    passToDoDueDateFilter,
    passSmartToDoFilters,
    getSmartToDoFilters,
    getCleanAssigneeFilter,
  };
};

module.exports = ResourceFilters();

const DateTime = require("../dateTime");
const GeoServerFilterFunctions = require("../mapFunctions/geoServerFilterFunctions");
const Filters = require("../mapFunctions/filters");
const DataViewFunctions = require("../dataViewFunctions");
